import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, ArrowRight, Check } from "lucide-react";
import { openWhatsAppChannel } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StayConnected — Temple of Kulture's primary conversion block.
 *
 * Two equally-weighted channels: the email newsletter and the
 * WhatsApp Channel. Both treated as first-class — neither styled
 * as a "secondary" option. The WhatsApp card uses TOK gold, NOT
 * WhatsApp green, so the brand stays cinematic.
 *
 * Variants:
 *   - "dual"      → two cards side-by-side (Home, Community)
 *   - "whatsapp"  → standalone WhatsApp card with contextual copy
 *                   (LEVEL UP, Home Ed, What's On, Ubuntu Retreat...)
 *   - "footer"    → compact inline button (used by Footer)
 */
export default function StayConnected({
  variant = "dual",
  pageSource,
  // Newsletter copy
  newsletterHeadline = "ENTER THE TEMPLE",
  newsletterTagline = "Be the first to step inside.",
  // WhatsApp copy
  whatsappHeadline = "STAY CLOSE TO THE FIRE",
  whatsappTagline = "Live drops, the moment they're announced.",
  bullets = DEFAULT_BULLETS,
  whatsappCta = "Join the WhatsApp Channel",
  testid = "stay-connected",
}) {
  if (variant === "footer") {
    return <FooterWhatsAppButton pageSource={pageSource} testid={testid} />;
  }

  if (variant === "whatsapp") {
    return (
      <section
        data-testid={`${testid}-whatsapp`}
        className="relative py-16 sm:py-20"
      >
        <div className="max-w-3xl mx-auto px-6">
          <WhatsAppCard
            headline={whatsappHeadline}
            tagline={whatsappTagline}
            bullets={bullets}
            cta={whatsappCta}
            pageSource={pageSource}
            testid={testid}
          />
        </div>
      </section>
    );
  }

  // default: dual
  return (
    <section
      data-testid={`${testid}-dual`}
      className="relative py-20 sm:py-28"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
            Stay connected to the Temple
          </p>
          <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
            Two doors. <span className="text-amber-400">Both open.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Pick the channel that fits your rhythm. No spam from either.
            Leave anytime.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <NewsletterCard
            headline={newsletterHeadline}
            tagline={newsletterTagline}
            pageSource={pageSource}
            testid={testid}
          />
          <WhatsAppCard
            headline={whatsappHeadline}
            tagline={whatsappTagline}
            bullets={bullets}
            cta={whatsappCta}
            pageSource={pageSource}
            testid={testid}
          />
        </div>
      </div>
    </section>
  );
}

const DEFAULT_BULLETS = [
  "Early access to immersive shows",
  "Ubuntu Retreat dates",
  "LEVEL UP opportunities",
  "Home Education updates",
  "Family events & workshops",
  "Festival announcements",
  "Casting calls & artist opportunities",
];

/* ------------------------------- Newsletter ------------------------------ */

function NewsletterCard({ headline, tagline, pageSource, testid }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email,
        page_source: pageSource || (typeof window !== "undefined" ? window.location.pathname : null),
        device_source: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setDone(true);
      toast.success("You're in. Watch your inbox for the next drop.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Couldn't subscribe — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`${testid}-newsletter-card`}
      className="relative bg-zinc-950 border border-white/10 hover:border-amber-400/40 transition-colors p-8 sm:p-10 flex flex-col"
    >
      <div className="inline-flex h-10 w-10 items-center justify-center bg-amber-400/10 border border-amber-400/30 text-amber-400">
        <Mail className="h-4 w-4" />
      </div>
      <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
        Email · Newsletter
      </p>
      <h3 className="mt-3 font-display font-black uppercase text-2xl sm:text-3xl leading-tight">
        {headline}
      </h3>
      <p className="mt-3 text-zinc-300 leading-relaxed text-sm">{tagline}</p>
      <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
        Behind-the-scenes drops, programme launches, and the occasional ritual.
      </p>

      <form
        onSubmit={submit}
        data-testid={`${testid}-newsletter-form`}
        className="mt-auto pt-8 flex flex-col sm:flex-row gap-2"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid={`${testid}-newsletter-email`}
          className="flex-1 bg-black border border-white/15 focus:border-amber-400 outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-500"
        />
        <button
          type="submit"
          disabled={loading || done}
          data-testid={`${testid}-newsletter-submit`}
          className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:bg-amber-400/40 text-black px-5 py-3 font-bold uppercase tracking-[0.22em] text-[11px] transition-colors"
        >
          {done ? (
            <>
              <Check className="h-3.5 w-3.5" /> In
            </>
          ) : (
            <>
              {loading ? "…" : "Subscribe"}
              <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      </form>
      <p className="mt-3 text-[10px] text-zinc-600 leading-relaxed">
        No spam. Unsubscribe anytime.
      </p>
    </div>
  );
}

/* -------------------------------- WhatsApp ------------------------------- */

function WhatsAppCard({
  headline,
  tagline,
  bullets,
  cta,
  pageSource,
  testid,
}) {
  const [clicked, setClicked] = useState(false);
  const handleJoin = () => {
    setClicked(true);
    openWhatsAppChannel({ pageSource });
  };

  return (
    <div
      data-testid={`${testid}-whatsapp-card`}
      className="relative overflow-hidden bg-gradient-to-br from-zinc-900 to-black border border-amber-400/30 hover:border-amber-400 transition-colors p-8 sm:p-10 flex flex-col"
    >
      {/* soft halo */}
      <div className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 bg-amber-400/[0.08] blur-3xl" />
      <div className="relative">
        <div className="inline-flex h-10 w-10 items-center justify-center bg-amber-400 text-black">
          <MessageCircle className="h-4 w-4" />
        </div>
        <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
          WhatsApp · Channel
        </p>
        <h3 className="mt-3 font-display font-black uppercase text-2xl sm:text-3xl leading-tight">
          {headline}
        </h3>
        <p className="mt-3 text-zinc-300 leading-relaxed text-sm">{tagline}</p>

        {bullets && bullets.length > 0 && (
          <ul className="mt-6 space-y-2">
            {bullets.slice(0, 7).map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 text-sm text-zinc-300"
              >
                <span className="mt-[7px] inline-block h-1.5 w-1.5 bg-amber-400 shrink-0" />
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={handleJoin}
          data-testid={`${testid}-whatsapp-cta`}
          className="mt-8 inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-amber-400 hover:bg-amber-300 text-black px-6 py-4 font-bold uppercase tracking-[0.22em] text-[11px] transition-colors"
        >
          <MessageCircle className="h-4 w-4" />
          {cta}
          <ArrowRight className="h-4 w-4" />
        </button>
        <p className="mt-3 text-[10px] text-zinc-500 leading-relaxed">
          {clicked
            ? "Channel opening in WhatsApp — tap Follow to stay close."
            : "Be the first to hear about new experiences. No spam. Leave anytime."}
        </p>
      </div>
    </div>
  );
}

/* ------------------------- Compact footer variant ------------------------ */

function FooterWhatsAppButton({ pageSource, testid }) {
  const [clicked, setClicked] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        setClicked(true);
        openWhatsAppChannel({ pageSource });
      }}
      data-testid={`${testid}-footer-whatsapp`}
      className="group inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 hover:bg-amber-400/[0.06] text-amber-300 hover:text-amber-200 px-5 py-3 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors"
      aria-label="Join the Temple of Kulture WhatsApp Channel"
    >
      <MessageCircle className="h-3.5 w-3.5" />
      {clicked ? "Channel opening…" : "Join WhatsApp Channel"}
      <ArrowRight className="h-3 w-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}
